/**
 * Global media validation configuration for Yoga School Admin Dashboard.
 * Centralizes all limits, accepted types, and validation logic for media uploads.
 */

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  IMAGE: 2 * 1024 * 1024,      // 2MB
  VIDEO: 20 * 1024 * 1024,     // 20MB
  DOCUMENT: 2 * 1024 * 1024,   // 2MB (PDF, images for verification docs)
} as const

// Accepted MIME types (include x-pdf for some browsers/OS that use it for PDFs)
export const ACCEPTED_MIME_TYPES = {
  IMAGE: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  VIDEO: ['video/mp4', 'video/webm'],
  DOCUMENT: ['application/pdf', 'application/x-pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
} as const

// Human-readable format strings for file inputs
export const ACCEPT_STRINGS = {
  IMAGE: 'image/jpeg,image/jpg,image/png,image/webp',
  VIDEO: 'video/mp4,video/webm',
  DOCUMENT: 'application/pdf,application/x-pdf,image/jpeg,image/jpg,image/png,image/webp',
} as const

/** Section identifiers for media upload contexts */
export type MediaSection =
  | 'courseMedia'
  | 'courseDescription'
  | 'schoolLogo'
  | 'schoolCoverImage'
  | 'schoolGallery'
  | 'schoolIntroVideo'
  | 'schoolVideoGallery'
  | 'roomImages'
  | 'roomVideo'
  | 'verificationDocument'
  | 'verificationDocumentAdditional'

/** Validation rules per section */
export interface MediaSectionConfig {
  maxImages?: number
  maxVideos?: number
  maxDocuments?: number
  maxImageSize?: number
  maxVideoSize?: number
  maxDocumentSize?: number
  acceptImages?: string
  acceptVideos?: string
  acceptDocuments?: string
  allowMultipleImages?: boolean
  allowMultipleVideos?: boolean
}

export const MEDIA_SECTIONS: Record<MediaSection, MediaSectionConfig> = {
  courseMedia: {
    maxImages: 5,
    maxVideos: 2,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
    allowMultipleImages: true,
    allowMultipleVideos: false,
  },
  courseDescription: {
    maxImages: 2, // embedded in description text
    maxVideos: 1,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
  },
  schoolLogo: {
    maxImages: 1,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    acceptImages: ACCEPT_STRINGS.IMAGE,
  },
  schoolCoverImage: {
    maxImages: 1,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    acceptImages: ACCEPT_STRINGS.IMAGE,
  },
  schoolGallery: {
    maxImages: 10,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    allowMultipleImages: true,
  },
  schoolIntroVideo: {
    maxVideos: 1,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
  },
  schoolVideoGallery: {
    maxVideos: 4,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
    allowMultipleVideos: true,
  },
  roomImages: {
    maxImages: 4,
    maxImageSize: FILE_SIZE_LIMITS.IMAGE,
    acceptImages: ACCEPT_STRINGS.IMAGE,
    allowMultipleImages: true,
  },
  roomVideo: {
    maxVideos: 1,
    maxVideoSize: FILE_SIZE_LIMITS.VIDEO,
    acceptVideos: ACCEPT_STRINGS.VIDEO,
  },
  verificationDocument: {
    maxDocuments: 10,
    maxDocumentSize: FILE_SIZE_LIMITS.DOCUMENT,
    acceptDocuments: ACCEPT_STRINGS.DOCUMENT,
    allowMultipleImages: false,
  },
  verificationDocumentAdditional: {
    maxDocuments: 5,
    maxDocumentSize: FILE_SIZE_LIMITS.DOCUMENT,
    acceptDocuments: ACCEPT_STRINGS.DOCUMENT,
  },
}

export interface ValidationResult {
  valid: boolean
  error?: string
}

/** Validate a single image file */
export function validateImageFile(
  file: File,
  section: MediaSection,
  currentCount: number = 0
): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxImages = config.maxImages ?? 0
  const maxSize = config.maxImageSize ?? FILE_SIZE_LIMITS.IMAGE

  if (!(ACCEPTED_MIME_TYPES.IMAGE as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error: `Invalid image type. Allowed: JPEG, PNG, WebP, GIF.`,
    }
  }

  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0)
    return {
      valid: false,
      error: `Image must be under ${maxMB}MB.`,
    }
  }

  if (maxImages > 0 && currentCount >= maxImages) {
    return {
      valid: false,
      error: `Maximum ${maxImages} image(s) allowed for this section.`,
    }
  }

  return { valid: true }
}

/** Validate a single video file */
export function validateVideoFile(
  file: File,
  section: MediaSection,
  currentCount: number = 0
): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxVideos = config.maxVideos ?? 0
  const maxSize = config.maxVideoSize ?? FILE_SIZE_LIMITS.VIDEO

  if (!(ACCEPTED_MIME_TYPES.VIDEO as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error: `Invalid video type. Allowed: MP4, WebM, QuickTime.`,
    }
  }

  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0)
    return {
      valid: false,
      error: `Video must be under ${maxMB}MB.`,
    }
  }

  if (maxVideos > 0 && currentCount >= maxVideos) {
    return {
      valid: false,
      error: `Maximum ${maxVideos} video(s) allowed for this section.`,
    }
  }

  return { valid: true }
}

/** Validate a document file (PDF or image) */
export function validateDocumentFile(
  file: File,
  section: MediaSection,
  currentCount: number = 0
): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxDocs = config.maxDocuments ?? 10
  const maxSize = config.maxDocumentSize ?? FILE_SIZE_LIMITS.DOCUMENT

  if (!(ACCEPTED_MIME_TYPES.DOCUMENT as readonly string[]).includes(file.type)) {
    return {
      valid: false,
      error: `Invalid document type. Allowed: PDF, JPEG, PNG, WebP.`,
    }
  }

  if (file.size > maxSize) {
    const maxMB = (maxSize / (1024 * 1024)).toFixed(0)
    return {
      valid: false,
      error: `Document must be under ${maxMB}MB.`,
    }
  }

  if (currentCount >= maxDocs) {
    return {
      valid: false,
      error: `Maximum ${maxDocs} document(s) allowed.`,
    }
  }

  return { valid: true }
}

/** Validate multiple image files for a section. Returns first error or all valid. */
export function validateImageFiles(
  files: File[],
  section: MediaSection,
  currentCount: number = 0
): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxImages = config.maxImages ?? 0
  const allowedCount = maxImages > 0 ? maxImages - currentCount : files.length

  if (files.length > allowedCount) {
    return {
      valid: false,
      error: `You can add at most ${allowedCount} more image(s). Maximum ${maxImages} for this section.`,
    }
  }

  for (let i = 0; i < files.length; i++) {
    const result = validateImageFile(files[i], section, currentCount + i)
    if (!result.valid) return result
  }

  return { valid: true }
}

/** Validate multiple video files for a section. */
export function validateVideoFiles(
  files: File[],
  section: MediaSection,
  currentCount: number = 0
): ValidationResult {
  const config = MEDIA_SECTIONS[section]
  const maxVideos = config.maxVideos ?? 0
  const allowedCount = maxVideos > 0 ? maxVideos - currentCount : files.length

  if (files.length > allowedCount) {
    return {
      valid: false,
      error: `You can add at most ${allowedCount} more video(s). Maximum ${maxVideos} for this section.`,
    }
  }

  for (let i = 0; i < files.length; i++) {
    const result = validateVideoFile(files[i], section, currentCount + i)
    if (!result.valid) return result
  }

  return { valid: true }
}
